import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import { type FC } from 'react';

import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { InternalLink } from '@/components/InternalLink';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  isOwner: boolean;
  hasCosigners: boolean;
  newOrderOnClick?: () => void;
}

export const FundOrderCompleted: FC<Props> = ({
  isOwner,
  hasCosigners,
  newOrderOnClick,
}) => {
  const texts = useTexts();
  const organizationCmId = useOrganizationCmId();
  const getTrackId = useGetTrackId();

  const alertText =
    isOwner && hasCosigners
      ? texts.orders.form.orderReceived.sentToCosigner
      : texts.orders.form.orderReceived.received;

  return (
    <WithGap gap="48">
      <h2 data-test="completed-step-header">
        {texts.orders.form.steps.finished.title}
      </h2>

      <Alert title={alertText} variant="success">
        <>
          {texts.orders.form.orderReceived.message}
          {/* Have a space between the message and the link text. */}{' '}
          <InternalLink
            to={`/${organizationCmId}/trading/orders`}
            data-trackid={getTrackId('go-to-order-list', TrackingElement.Link)}
          >
            {texts.orders.form.orderReceived.linkText}
          </InternalLink>
        </>
      </Alert>

      <WithGap gap="8" direction="row">
        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/trading/subscription`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-subscription-order"
          data-trackid={getTrackId('go-to-subscription', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.buy}
        </Button>

        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/trading/redemption`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-redemption-order"
          data-trackid={getTrackId('go-to-redemption', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.sell}
        </Button>

        <Button
          variant="subtle"
          type="button"
          to={`/${organizationCmId}/trading/switch`}
          as={InternalLink}
          onClick={newOrderOnClick}
          data-test="new-switch-order"
          data-trackid={getTrackId('go-to-switch', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.switch}
        </Button>

        <Button
          variant="tertiary"
          iconRight="arrow_right"
          type="button"
          to={`/${organizationCmId}`}
          as={InternalLink}
          data-trackid={getTrackId('go-to-overview', TrackingElement.Link)}
        >
          {texts.orders.form.buttons.overview}
        </Button>
      </WithGap>
    </WithGap>
  );
};
